import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const { width, height } = Dimensions.get('window');

interface InteractiveSplitManagementGuideProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Interactive Tutorial for Managing Revenue Splits
 * 
 * This tutorial provides a step-by-step, interactive guide for managing revenue splits.
 * It highlights specific UI elements and explains their functionality in context.
 * 
 * How it works:
 * 1. The tutorial overlays on top of the actual UI
 * 2. Each step highlights a specific element (using positioning)
 * 3. Users can navigate through steps at their own pace
 * 4. The tutorial provides contextual tips and best practices
 * 
 * Integration:
 * - Trigger from RevenueSplitManager via a "?" help icon
 * - Can be dismissed at any time
 * - Remembers if user has seen it before
 * 
 * Steps covered:
 * 1. Overview of the split manager interface
 * 2. Adding a new partner (+ button)
 * 3. Entering partner details (email, percentage)
 * 4. Understanding effective rate calculation
 * 5. Checking partner payout status
 * 6. Saving changes
 * 7. Copying splits from another charger
 * 8. Removing a split
 */

const guideSteps = [
  {
    id: 1,
    title: 'Revenue Split Manager',
    icon: 'people-outline' as const,
    description: 'This is where you manage all revenue splits for your charger.',
    highlights: [
      'View all current partners',
      'Add new revenue splits',
      'See your effective earning rate',
      'Monitor total split percentage',
    ],
    tip: 'Keep your total splits under 100% to maintain positive earnings!',
  },
  {
    id: 2,
    title: 'Adding a Partner',
    icon: 'add-circle-outline' as const,
    description: 'Click the + button to add a new partner split.',
    highlights: [
      'Each split requires partner email and percentage',
      'You can add multiple partners',
      'Splits are calculated per transaction',
    ],
    tip: 'Make sure to enter the correct partner email - they will receive notifications about the split.',
  },
  {
    id: 3,
    title: 'Split Percentage',
    icon: 'calculator-outline' as const,
    description: 'Set the percentage this partner will receive (0.01 - 100%).',
    highlights: [
      'Minimum split: 0.01%',
      'Maximum split: 100%',
      'Total splits cannot exceed 100%',
      'Percentages are calculated on gross revenue',
    ],
    tip: 'Common split percentages: 5-15% for referral partners, 20-40% for co-owners, 50-50% for equal partnerships.',
  },
  {
    id: 4,
    title: 'Effective Rate',
    icon: 'trending-down-outline' as const,
    description: 'Your effective rate shows what you earn per kWh after splits.',
    highlights: [
      'Calculated as: Base Rate × (1 - Total Split %)',
      'Updates automatically as you add/remove splits',
      'Helps you understand your net earnings',
    ],
    tip: 'If your effective rate seems too low, consider adjusting split percentages.',
  },
  {
    id: 5,
    title: 'Payout Status',
    icon: 'checkmark-circle-outline' as const,
    description: 'See if your partner is ready to receive payouts.',
    highlights: [
      '✓ Ready: Partner has payout account configured',
      '⚠ Needs Setup: Partner must configure payouts',
      'Earnings are held until payout is set up',
    ],
    tip: 'Encourage partners to set up their payout accounts to avoid delayed payments.',
  },
  {
    id: 6,
    title: 'Saving Changes',
    icon: 'save-outline' as const,
    description: 'Your split changes are saved automatically when you update the charger.',
    highlights: [
      'New splits: Created on save',
      'Modified splits: Updated on save',
      'Removed splits: Requires confirmation',
    ],
    tip: 'Double-check all percentages before saving!',
  },
  {
    id: 7,
    title: 'Copy Splits',
    icon: 'copy-outline' as const,
    description: 'Quickly replicate split configurations from another charger.',
    highlights: [
      'Select source charger',
      'Splits are copied instantly',
      'You can still modify after copying',
      'Great for maintaining consistent partnerships',
    ],
    tip: 'Use this feature when setting up multiple chargers with the same partners.',
  },
  {
    id: 8,
    title: 'Removing Splits',
    icon: 'trash-outline' as const,
    description: 'Remove a partner split using the - button.',
    highlights: [
      'Existing splits: Requires confirmation',
      'New unsaved splits: Removed immediately',
      'Revoked splits are logged for records',
    ],
    tip: 'Communicate with partners before removing their splits to maintain good relationships.',
  },
];

const InteractiveSplitManagementGuide: React.FC<InteractiveSplitManagementGuideProps> = ({
  visible,
  onClose,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < guideSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const step = guideSteps[currentStep];
  const progress = ((currentStep + 1) / guideSteps.length) * 100;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Ionicons name="help-circle" size={24} color="#3B82F6" />
              <Text style={styles.headerTitle}>Interactive Guide</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color="#8D8E90" />
            </TouchableOpacity>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress}%` }]} />
          </View>

          {/* Content */}
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.iconContainer}>
              <Ionicons name={step.icon} size={48} color="#3B82F6" />
            </View>

            <Text style={styles.stepTitle}>{step.title}</Text>
            <Text style={styles.stepDescription}>{step.description}</Text>

            <View style={styles.highlightsSection}>
              <Text style={styles.highlightsTitle}>Key Points:</Text>
              {step.highlights.map((highlight, index) => (
                <View key={index} style={styles.highlightItem}>
                  <View style={styles.bullet} />
                  <Text style={styles.highlightText}>{highlight}</Text>
                </View>
              ))}
            </View>

            {step.tip && (
              <View style={styles.tipContainer}>
                <Ionicons name="bulb" size={20} color="#F59E0B" />
                <View style={styles.tipContent}>
                  <Text style={styles.tipLabel}>Pro Tip:</Text>
                  <Text style={styles.tipText}>{step.tip}</Text>
                </View>
              </View>
            )}
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              onPress={handlePrevious}
              style={[
                styles.button,
                styles.secondaryButton,
                currentStep === 0 && styles.buttonDisabled,
              ]}
              disabled={currentStep === 0}
            >
              <Ionicons
                name="arrow-back"
                size={20}
                color={currentStep === 0 ? '#8D8E90' : '#3B82F6'}
              />
              <Text
                style={[
                  styles.buttonText,
                  styles.secondaryButtonText,
                  currentStep === 0 && styles.buttonTextDisabled,
                ]}
              >
                Previous
              </Text>
            </TouchableOpacity>

            <View style={styles.stepIndicator}>
              <Text style={styles.stepIndicatorText}>
                {currentStep + 1} / {guideSteps.length}
              </Text>
            </View>

            <TouchableOpacity
              onPress={handleNext}
              style={[styles.button, styles.primaryButton]}
            >
              <Text style={[styles.buttonText, styles.primaryButtonText]}>
                {currentStep === guideSteps.length - 1 ? 'Done' : 'Next'}
              </Text>
              <Ionicons name="arrow-forward" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#292D33',
    borderRadius: 16,
    width: Math.min(width - 40, 450),
    maxHeight: height - 120,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1D23',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  closeButton: {
    padding: 4,
  },
  progressContainer: {
    height: 3,
    backgroundColor: '#1A1D23',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#3B82F6',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    marginBottom: 16,
  },
  stepTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  stepDescription: {
    color: '#E8E8E9',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  highlightsSection: {
    marginBottom: 16,
  },
  highlightsTitle: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  highlightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
    paddingLeft: 8,
  },
  bullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#3B82F6',
    marginTop: 6,
    marginRight: 10,
  },
  highlightText: {
    color: '#E8E8E9',
    fontSize: 13,
    flex: 1,
    lineHeight: 18,
  },
  tipContainer: {
    flexDirection: 'row',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    borderLeftWidth: 3,
    borderLeftColor: '#F59E0B',
    borderRadius: 8,
    padding: 12,
    gap: 10,
  },
  tipContent: {
    flex: 1,
  },
  tipLabel: {
    color: '#F59E0B',
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 4,
  },
  tipText: {
    color: '#E8E8E9',
    fontSize: 12,
    lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#1A1D23',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  primaryButton: {
    backgroundColor: '#3B82F6',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  buttonDisabled: {
    borderColor: '#8D8E90',
    opacity: 0.5,
  },
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  primaryButtonText: {
    color: '#fff',
  },
  secondaryButtonText: {
    color: '#3B82F6',
  },
  buttonTextDisabled: {
    color: '#8D8E90',
  },
  stepIndicator: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#1A1D23',
    borderRadius: 10,
  },
  stepIndicatorText: {
    color: '#E8E8E9',
    fontSize: 11,
    fontWeight: '600',
  },
});

export default InteractiveSplitManagementGuide;

